<template>
  <div
    class="cart-gift-note critical-component-clear"
    :class="{
      'cart-gift-note--is-editing' : isEditing,
      'cart-gift-note--has-note' : storedNote || isSaving,
      'cart-gift-note--main-cart' : $variable('request.pageType') === 'cart',
    }"
  >
    <!-- Gift note toggle -->
    <button
      type="button"
      class="text-body-s-regular cart-gift-note__toggle mb-s"
      :class="{
        'mt-s' : $variable('request.pageType') === 'cart',
      }"
      @click.prevent="toggleEditing"
    >
      <icon-gift />
      <span
        class="cart-gift-note__text ml-2xs"
        v-text="storedNote
          ? $string('cart.gift_note.view_label')
          : $string('cart.gift_note.add_label')"
      />
      <icon-arrow-down />
    </button>

    <transition
      name="fade"
      :duration="$timing('quick')"
    >
      <div v-show="isEditing">
        <!-- Text box -->
        <textarea
          ref="note"
          v-model="note"
          class="cart-gift-note__note"
          :placeholder="$string('cart.gift_note.placeholder')"
          maxlength="250"
          @keyup="autoSaveNote"
        />

        <div class="cart-gift-note__actions">
          <!-- Delete -->
          <button
            v-show="note"
            class="cart-gift-note__action"
            type="button"
            @click.prevent="deleteNote"
            v-text="$string('cart.gift_note.delete_label')"
          />

          <!-- Save -->
          <button
            v-show="note && note !== storedNote"
            class="cart-gift-note__action"
            type="button"
            @click.prevent="saveNote"
            v-text="$string('cart.gift_note.save_label')"
          />

          <!-- Save Success -->
          <button
            v-show="note && note === storedNote"
            class="cart-gift-note__action cart-gift-note__action--success"
            type="button"
            @click.prevent="toggleEditing"
            v-text="$string('cart.gift_note.save_success_label')"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: cart-gift-note (cart-gift-note)
 * -----------------------------------------------------------------------------
 * Gift note component, used to manage gift note attribute.
 *
 * @param {?String} savedNote - The saved gift note attribute if present.
 */

import { mapActions, mapState } from 'vuex'

import IconGift from '~icons/general/gift.svg'
import IconArrowDown from '~icons/directional-navigation/arrow/down.svg'

export default {
  name: 'CartGiftNote',

  components: {
    IconGift,
    IconArrowDown,
  },

  data() {
    return {
      debounce: null,
      debounceTimer: 1500,
      note: null,
      isTyping: false,
      isEditing: false,
      isSaving: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      settings: (state) => state.index.settings,
    }),

    /**
     * Compute stored gift note
     * @returns {String|undefined}
     */
    storedNote() {
      return this.cart.attributes?.gift_note
    },
  },

  watch: {

    /**
     * Update cart drawer focus trap to enable tab targeting on
     * save and delete buttons
     * @returns {Promise<void>}
     */
    async note() {
      if (this.isTyping) {
        return
      }
      await this.$nextTick()

      cnvs.EventBus.emit('overlay:update', {
        namespace: 'cartDrawer',
        currentElement: document.activeElement,
      })
    },

    /**
     * Update note when cart attribute gift note changes
     */
    storedNote() {
      if (!this.isTyping) {
        this.note = this.storedNote
      }
    },
  },

  mounted() {
    this.note = this.storedNote
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateCartAttributes: 'cart/updateCartAttributes',
    }),

    /**
     * Hide / show the edit controls
     */
    async toggleEditing() {
      this.isEditing = !this.isEditing

      if (this.isEditing) {
        await this.$nextTick()
        this.$refs.note.focus()
      }
    },

    /**
     * Set isSaving
     */
    setIsSaving(isSaving) {
      this.isSaving = isSaving
    },

    /**
     * Trigger save automatically on note update
     */
    autoSaveNote() {
      this.isTyping = true

      if (this.debounce) {
        clearTimeout(this.debounce)
        this.debounce = null
      }

      this.debounce = setTimeout(() => {
        this.saveNote()
      }, this.debounceTimer)
    },

    /**
     * Save the note as a cart attribute
     */
    saveNote() {
      this.setIsSaving(true)

      this.updateCartAttributes({
        attributes: {
          gift_note: this.note,
        },
      }).finally(() => {
        this.setIsSaving(false)
        this.isTyping = false
      })
    },

    /**
     * Delete the note locally, and the cart attribute
     */
    deleteNote() {
      this.note = null
      this.updateCartAttributes({
        attributes: {
          gift_note: this.note,
        },
      })
      this.isEditing = false
    },
  },
}
</script>

<style lang="scss">
@import './cart-gift-note';
</style>
